import {renderStructuredElements} from "@/utils/render-helpers.jsx";
import {product as productStore} from "@/store/Product";
import {price as priceStore} from "@/store/Price";
import JSON5 from "json5";
import ProductFormV2 from "@/components/ProductPage/ProductFormV2";

// Dedicated entrypoint for the pdp-v2 buy box + the collagen product-toggle. Leaves the
// v1 product.jsx untouched. main-product-v2.liquid loads this via vite-tag.

const initProductStore = () => {
  const el = document.querySelector('[data-store-id="product"]');
  if (!el) return;
  const data = JSON5.parse(el.dataset.storeData);
  productStore.setVariantId(data.variantId);
};

const initPriceStore = () => {
  const el = document.querySelector('[data-store-id="price"]');
  if (!el) return;
  const data = JSON5.parse(el.dataset.storeData);
  priceStore.setPricesData(data);
};

const mountProduct = () => {
  initProductStore();
  initPriceStore();
  renderStructuredElements("product-form-v2", ProductFormV2, {
    stores: {productStore, priceStore},
  });
};

// --- Full-page in-place product swap ------------------------------------------------
// The 3 collagen products each have their own server-rendered page/template. To make
// EVERY product-specific section update on toggle (not just the buy box) without a full
// reload, we fetch the target product's page, replace this section's DOM, re-run its
// inline scripts, and re-mount the React buy box. Any failure falls back to a normal
// navigation so the user always lands on the right page.
const sectionOf = () =>
  document.querySelector(".pdp-v2")?.closest('[id^="shopify-section-"]');

let cgSwapping = false;

async function swapProductPage(url, push = true) {
  if (cgSwapping || !url) return;

  // Theme editor only: the client-side swap below (replaceWith a section with a DIFFERENT
  // section id + pushState) fights the editor, which owns section-by-id rendering and preview
  // navigation. It leaves the DOM out of sync with the editor's server-side page context —
  // showing stale/mixed content, not updating, or restoring the wrong product after a reload.
  // In design mode just navigate the preview; the editor reloads the target product cleanly.
  if (window.Shopify && window.Shopify.designMode) {
    window.location.href = url;
    return;
  }

  const current = sectionOf();
  if (!current) {
    window.location.href = url;
    return;
  }
  cgSwapping = true;

  // Keep the buy box visually anchored across the swap (sections above it may differ in height).
  const anchor = document.getElementById("buybox");
  const anchorTopBefore = anchor ? anchor.getBoundingClientRect().top : null;

  current.style.transition = "opacity 0.15s ease";
  current.style.opacity = "0.5";
  current.style.pointerEvents = "none";
  current.setAttribute("aria-busy", "true");

  try {
    const res = await fetch(url, {credentials: "same-origin"});
    if (!res.ok) throw new Error(`status ${res.status}`);
    const html = await res.text();
    const doc = new DOMParser().parseFromString(html, "text/html");
    const next = doc.querySelector(".pdp-v2")?.closest('[id^="shopify-section-"]');
    if (!next) throw new Error("pdp-v2 section not found in fetched page");

    if (push) window.history.pushState({cgUrl: url}, "", url);
    current.replaceWith(next);

    // Re-run inline scripts — nodes inserted via DOM APIs don't execute their <script>s.
    // Skip external/module scripts: the JS bundle + third-party loaders are already loaded,
    // and re-loading the module would double-bind this entrypoint's listeners.
    next.querySelectorAll("script").forEach(old => {
      if (old.src || old.type === "module") return;
      const s = document.createElement("script");
      if (old.type) s.type = old.type;
      s.textContent = old.textContent;
      old.replaceWith(s);
    });

    mountProduct();

    // Re-anchor the scroll so the buy box stays put.
    const anchorAfter = document.getElementById("buybox");
    if (anchorTopBefore != null && anchorAfter) {
      window.scrollBy(0, anchorAfter.getBoundingClientRect().top - anchorTopBefore);
    }
  } catch (e) {
    console.error("[cg] product swap failed — navigating instead", e);
    window.location.href = url;
  } finally {
    cgSwapping = false;
  }
}

window.__cgSwapProduct = swapProductPage;

document.addEventListener("DOMContentLoaded", mountProduct);

// Browser Back/Forward between toggled products: swap to the URL without pushing again.
// Skip in the theme editor — it owns preview history/navigation (we don't pushState there).
window.addEventListener("popstate", () => {
  if (window.Shopify && window.Shopify.designMode) return;
  if (document.querySelector(".pdp-v2") && window.location.pathname.indexOf("/products/") !== -1) {
    swapProductPage(window.location.href, false);
  }
});

// Delegated: any <a data-cg-swap [data-cg-view="<suffix>"]> (e.g. the comparison-table
// "wählen" CTAs) swaps in place like the toggle instead of navigating. Bound once here.
document.addEventListener("click", e => {
  const link = e.target.closest && e.target.closest("a[data-cg-swap]");
  if (!link) return;
  const href = link.getAttribute("href");
  if (!href || href.charAt(0) === "#") return;
  e.preventDefault();
  const view = link.getAttribute("data-cg-view");
  const base = href.split("?")[0];
  const inPreview = new URLSearchParams(window.location.search).has("view");
  swapProductPage(inPreview && view ? `${base}?view=${view}` : base);
});

// Theme editor re-renders section HTML without re-running DOMContentLoaded; re-mount then.
document.addEventListener("shopify:section:load", event => {
  const section = event.target;
  if (section && section.querySelector && section.querySelector('[data-id="product-form-v2"]')) {
    mountProduct();
  }
});
